/**
 * WebSocket 群聊适配器
 *
 * 实现 MessageServiceAdapter 接口，连接后端 /ws/chat 进行实时群聊通信
 *
 * 协议：
 *   - 连接: ws://host:3002/ws/chat?userId=X&nickname=Y&avatar=Z
 *   - 加入群: { type: 'join', payload: { groupId } }
 *   - 发送消息: { type: 'message', payload: { segments } }
 *   - 查询历史: { type: 'history', payload: { limit, before } }
 *   - 心跳: { type: 'heartbeat' }
 *   - 标记已读: { type: 'mark_read', payload: { messageId } }
 */

import type {
  GroupMessage,
  MessageServiceAdapter,
  WSConnectionStatus,
  ContentSegment,
} from '../types';

const WS_URL = (() => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host =
    process.env.NODE_ENV === 'production' ? window.location.host : 'localhost:3002';
  return `${protocol}//${host}/ws/chat`;
})();

const DEFAULT_GROUP_ID = 1;

export const createWSAdapter = (
  userId: string,
  nickname: string,
  avatar: string
): MessageServiceAdapter => {
  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectCount = 0;
  let joined = false;
  const MAX_RECONNECT = 5;
  const RECONNECT_BASE_DELAY = 1000;

  const adapter: MessageServiceAdapter = {
    onMessage: null,
    onHistoryLoaded: null,
    onStatusChange: null,

    connect(_userId?: string) {
      if (ws && ws.readyState === WebSocket.OPEN) return;

      adapter.onStatusChange?.('connecting');

      try {
        const url = `${WS_URL}?userId=${encodeURIComponent(userId)}&nickname=${encodeURIComponent(nickname)}`;
// avatar 不放在 URL 中（base64 太长会导致 URL 超出 HTTP 头长度限制），通过 join 消息发送
        ws = new WebSocket(url);

        ws.onopen = () => {
          reconnectCount = 0;
          // 加入默认群聊
          ws!.send(
            JSON.stringify({
              type: 'join',
              payload: { groupId: DEFAULT_GROUP_ID, avatar },
            })
          );

          // 心跳
          heartbeatTimer = setInterval(() => {
            if (ws?.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'heartbeat' }));
            }
          }, 25000);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleServerMessage(data);
          } catch {
            console.error('[WS] 消息解析失败');
          }
        };

        ws.onclose = () => {
          joined = false;
          adapter.onStatusChange?.('disconnected');
          clearInterval(heartbeatTimer!);
          heartbeatTimer = null;

          if (reconnectCount < MAX_RECONNECT) {
            const delay = RECONNECT_BASE_DELAY * Math.pow(2, reconnectCount);
            console.log(
              `[WS] ${delay}ms 后重连 (${reconnectCount + 1}/${MAX_RECONNECT})`
            );
            reconnectTimer = setTimeout(() => {
              reconnectCount++;
              adapter.connect(userId);
            }, delay);
          }
        };

        ws.onerror = () => {
          // onclose 会处理
        };
      } catch (err) {
        console.error('[WS] 连接失败:', err);
        adapter.onStatusChange?.('disconnected');
      }
    },

    disconnect() {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      reconnectCount = MAX_RECONNECT;
      if (ws) {
        ws.close(1000, '用户主动断开');
        ws = null;
      }
      joined = false;
    },

    send(msg: { segments: ContentSegment[]; senderId: string; senderName: string; senderAvatar: string }) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: 'message', payload: { segments: msg.segments } }));
    },

    loadHistory(params?: { groupId?: number; limit?: number; before?: string }) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        JSON.stringify({
          type: 'history',
          payload: {
            groupId: params?.groupId ?? DEFAULT_GROUP_ID,
            limit: params?.limit ?? 50,
            before: params?.before,
          },
        })
      );
    },
  };

  /**
   * 处理服务端消息
   */
  function handleServerMessage(data: any) {
    const { type, payload } = data;

    switch (type) {
      case 'connected':
        console.log('[WS] 连接确认:', payload);
        break;

      case 'joined':
        joined = true;
        adapter.onStatusChange?.('connected');
        // 加入成功后立即加载历史消息
        adapter.loadHistory?.({});
        break;

      case 'new_message': {
        const msg: GroupMessage = {
          id: payload.id,
          segments: payload.segments,
          senderId: String(payload.senderId),
          senderName: payload.senderName,
          senderAvatar: payload.senderAvatar || '',
          timestamp: payload.timestamp,
          status: 'sent',
        };
        adapter.onMessage?.(msg);
        break;
      }

      case 'system': {
        const sysMsg: GroupMessage = {
          id: payload.id,
          segments: [{ type: 'text', content: payload.content }],
          senderId: 'system',
          senderName: '系统消息',
          senderAvatar: '',
          timestamp: payload.timestamp,
          status: 'sent',
        };
        adapter.onMessage?.(sysMsg);
        break;
      }

      case 'history': {
        const list: GroupMessage[] = (payload.list || []).map((m: any) => ({
          id: m.id,
          segments: typeof m.segments === 'string' ? JSON.parse(m.segments) : m.segments,
          senderId: String(m.senderId),
          senderName: m.senderName,
          senderAvatar: m.senderAvatar || '',
          timestamp: m.timestamp,
          status: 'sent' as const,
        }));
        adapter.onHistoryLoaded?.(list);
        break;
      }

      case 'online_users':
        console.log('[WS] 在线用户:', payload);
        break;

      case 'heartbeat':
        break;

      case 'error':
        console.error('[WS] 服务端错误:', payload);
        break;
    }
  }

  return adapter;
};